import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO } from '../lib/format';
import { calcularFinanzas } from '../lib/finanzas';
import { gananciaVenta } from '../lib/negocio';
import MovimientosPorMes from './MovimientosPorMes';
import ProximosPagos from './ProximosPagos';
import { calcularProximosPagos } from '../lib/proximos';

function Dashboard({ userId }) {
  const [data, setData] = useState({ bolsillos: [], movimientos: [], compromisos: [], abonos: [], abonosMeta: [], deudas: [], pagosDeuda: [], prestamos: [], cobros: [], ventasMes: [], gastosFijos: [], envioRetiros: [], envioSemanas: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) return;
    const inicioMes = hoyISO().slice(0, 8) + '01';
    const load = async () => {
      const res = await Promise.all([
        supabase.from('bolsillos').select('*').eq('user_id', userId),
        supabase.from('bolsillo_movimientos').select('*').eq('user_id', userId),
        supabase.from('compromisos').select('*').eq('user_id', userId),
        supabase.from('abonos').select('*').eq('user_id', userId),
        supabase.from('abonos_meta').select('*').eq('user_id', userId),
        supabase.from('deudas').select('*').eq('user_id', userId).eq('activa', true),
        supabase.from('pagos_deuda').select('*').eq('user_id', userId),
        supabase.from('prestamos').select('*').eq('user_id', userId).eq('cobrado', false),
        supabase.from('cobros_prestamo').select('*').eq('user_id', userId),
        supabase.from('ventas').select('*').eq('user_id', userId).gte('fecha', inicioMes),
        supabase.from('gastos_fijos').select('*').eq('user_id', userId),
        supabase.from('envio_retiros').select('*').eq('user_id', userId),
        supabase.from('envio_semanas').select('*').eq('user_id', userId),
      ]);
      const conError = res.find(r => r.error);
      if (conError) setError('Error cargando datos: ' + conError.error.message);
      const [b, m, c, a, am, d, pd, pr, co, v, gf, er, es] = res.map(r => r.data || []);
      setData({
        bolsillos: b, movimientos: m, compromisos: c, abonos: a, abonosMeta: am, deudas: d, pagosDeuda: pd,
        prestamos: pr, cobros: co, ventasMes: v, gastosFijos: gf, envioRetiros: er, envioSemanas: es,
      });
      setLoading(false);
    };
    load();
  }, [userId]);

  const { bolsillos, movimientos, deudas, pagosDeuda, prestamos, cobros, ventasMes } = data;
  const fin = calcularFinanzas(data);
  const { totalSaldo, totalComprometido, totalEnMetas, totalDisponible, enRojo: bolsillosEnRojo } = fin;

  const totalDeudaPendiente = deudas.reduce((s, d) => {
    const pagado = pagosDeuda.filter(p => p.deuda_id === d.id).reduce((ps, p) => ps + Number(p.monto), 0);
    return s + Math.max(0, (d.monto_original || 0) - pagado);
  }, 0);
  const totalCuotasMensuales = deudas.reduce((s, d) => s + (d.cuota_mensual || 0), 0);
  const totalPorCobrar = prestamos.reduce((s, p) => {
    const cobrado = cobros.filter(c => c.prestamo_id === p.id).reduce((sc, c) => sc + Number(c.monto), 0);
    return s + Math.max(0, (p.monto || 0) - cobrado);
  }, 0);
  // Las devueltas no cuentan, y las de Meli sin monto tampoco (solo movieron stock)
  const ventasValidas = ventasMes.filter(v => !v.devuelta && Number(v.precio_unitario) > 0);
  const vendidoMes = ventasValidas.reduce((s, v) => s + v.cantidad * v.precio_unitario, 0);
  const gananciaMes = ventasValidas.reduce((s, v) => s + gananciaVenta(v), 0);
  const proximos = calcularProximosPagos(data);

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h2>Dashboard</h2>
          <p className="page-subtitle">Tu panorama financiero de un vistazo</p>
        </div>
      </div>

      {error && (
        <div className="alerta-rojo" style={{ marginBottom: 16 }}>
          <span className="alerta-rojo-icon">⚠️</span><div>{error}</div>
        </div>
      )}

      <ProximosPagos proximos={proximos} disponible={totalDisponible} porCobrar={totalPorCobrar} />

      {/* Bloque principal: los números que deben cuadrar */}
      <div className="dashboard-balance-block">
        <div className="db-saldo-total">
          <span className="db-label">Saldo total en cuentas</span>
          <span className="db-value-main">{formatCLP(totalSaldo)}</span>
          <span className="db-hint">Lo que tienes registrado en todas tus categorías</span>
        </div>
        <div className="db-breakdown">
          <div className="db-item db-disponible">
            <span className="db-item-icon">{totalDisponible >= 0 ? '✅' : '⚠️'}</span>
            <div>
              <span className="db-item-label">Disponible real</span>
              <span className={`db-item-value ${totalDisponible >= 0 ? 'text-success' : 'text-danger'}`}>{formatCLP(totalDisponible)}</span>
              <span className="db-item-hint">Puedes gastar esto libremente</span>
            </div>
          </div>
          <div className="db-divider">+</div>
          <div className="db-item db-comprometido">
            <span className="db-item-icon">📌</span>
            <div>
              <span className="db-item-label">Comprometido</span>
              <span className="db-item-value text-warning">{formatCLP(totalComprometido)}</span>
              <span className="db-item-hint">Gastos pendientes reservados</span>
            </div>
          </div>
          {totalEnMetas > 0 && (
            <>
              <div className="db-divider">+</div>
              <div className="db-item" style={{ background: 'var(--primary-xlight)' }}>
                <span className="db-item-icon">🎯</span>
                <div>
                  <span className="db-item-label">En metas</span>
                  <span className="db-item-value" style={{ color: 'var(--primary)' }}>{formatCLP(totalEnMetas)}</span>
                  <span className="db-item-hint">Reservado para tus ahorros</span>
                </div>
              </div>
            </>
          )}
          <div className="db-divider db-equals">=</div>
          <div className="db-item db-total-check">
            <span className="db-item-icon">💰</span>
            <div>
              <span className="db-item-label">Total en banco</span>
              <span className="db-item-value">{formatCLP(totalSaldo)}</span>
            </div>
          </div>
        </div>
        {bolsillosEnRojo.length > 0 && (
          <div style={{ marginTop: 12, padding: '8px 14px', background: '#fee2e2', borderRadius: 8, fontSize: '0.85rem', color: '#7f1d1d' }}>
            ⚠ {bolsillosEnRojo.length} categoría{bolsillosEnRojo.length > 1 ? 's' : ''} en rojo: {bolsillosEnRojo.map(b => b.nombre).join(', ')}
          </div>
        )}
      </div>

      {/* Mi negocio este mes */}
      {ventasMes.length > 0 && (
        <div className="dashboard-deudas-block" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="db-deudas-header">
            <span className="db-deudas-icon">🏷️</span>
            <div>
              <span className="db-deudas-title">Mi negocio este mes</span>
              <span className="db-deudas-sub">{ventasMes.length} venta{ventasMes.length > 1 ? 's' : ''}</span>
            </div>
            <div className="db-deudas-totals">
              <span className="db-deudas-total-label">Vendido: <strong>{formatCLP(vendidoMes)}</strong></span>
              <span className={`db-deudas-total-value ${gananciaMes >= 0 ? 'text-success' : 'text-danger'}`}>{formatCLP(gananciaMes)}</span>
              <span className="db-deudas-cuota">ganancia real</span>
            </div>
          </div>
        </div>
      )}

      {/* Deudas activas */}
      {deudas.length > 0 && (
        <div className="dashboard-deudas-block">
          <div className="db-deudas-header">
            <span className="db-deudas-icon">💳</span>
            <div>
              <span className="db-deudas-title">Deudas activas</span>
              <span className="db-deudas-sub">{deudas.length} deuda{deudas.length > 1 ? 's' : ''} pendiente{deudas.length > 1 ? 's' : ''}</span>
            </div>
            <div className="db-deudas-totals">
              <span className="db-deudas-total-label">Total pendiente</span>
              <span className="db-deudas-total-value text-danger">{formatCLP(totalDeudaPendiente)}</span>
              {totalCuotasMensuales > 0 && (
                <span className="db-deudas-cuota">Cuotas este mes: <strong>{formatCLP(totalCuotasMensuales)}</strong></span>
              )}
            </div>
          </div>
          <ul className="db-deudas-list">
            {deudas.map(d => {
              const pagado = pagosDeuda.filter(p => p.deuda_id === d.id).reduce((s, p) => s + Number(p.monto), 0);
              const pendiente = Math.max(0, (d.monto_original || 0) - pagado);
              const pct = d.monto_original > 0 ? Math.round((pagado / d.monto_original) * 100) : 0;
              return (
                <li key={d.id} className="db-deuda-item">
                  <span className="db-deuda-icon">{d.icono || '💳'}</span>
                  <div className="db-deuda-info">
                    <span className="db-deuda-nombre">{d.nombre}{d.acreedor ? ` — ${d.acreedor}` : ''}</span>
                    <div className="db-deuda-bar-wrap">
                      <div className="db-deuda-bar" style={{ width: `${pct}%`, background: pct > 75 ? 'var(--success)' : pct > 40 ? 'var(--warning)' : 'var(--danger)' }} />
                    </div>
                  </div>
                  <div className="db-deuda-amounts">
                    <span className="text-danger" style={{ fontWeight: 600 }}>{formatCLP(pendiente)}</span>
                    {d.cuota_mensual > 0 && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{formatCLP(d.cuota_mensual)}/mes</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Me Deben */}
      {prestamos.length > 0 && (
        <div className="dashboard-deudas-block" style={{ borderLeft: '4px solid #16a34a' }}>
          <div className="db-deudas-header">
            <span className="db-deudas-icon">🤝</span>
            <div>
              <span className="db-deudas-title">Me deben</span>
              <span className="db-deudas-sub">{prestamos.length} préstamo{prestamos.length > 1 ? 's' : ''} pendiente{prestamos.length > 1 ? 's' : ''}</span>
            </div>
            <div className="db-deudas-totals">
              <span className="db-deudas-total-label">Total por cobrar</span>
              <span className="db-deudas-total-value text-success">{formatCLP(totalPorCobrar)}</span>
            </div>
          </div>
          <ul className="db-deudas-list">
            {prestamos.map(p => {
              const cobrado = cobros.filter(c => c.prestamo_id === p.id).reduce((s, c) => s + Number(c.monto), 0);
              const pendiente = Math.max(0, (p.monto || 0) - cobrado);
              const pct = p.monto > 0 ? Math.round((cobrado / p.monto) * 100) : 0;
              return (
                <li key={p.id} className="db-deuda-item">
                  <span className="db-deuda-icon">🤝</span>
                  <div className="db-deuda-info">
                    <span className="db-deuda-nombre">{p.persona}</span>
                    <div className="db-deuda-bar-wrap">
                      <div className="db-deuda-bar" style={{ width: `${pct}%`, background: '#16a34a' }} />
                    </div>
                  </div>
                  <div className="db-deuda-amounts">
                    <span className="text-success" style={{ fontWeight: 600 }}>{formatCLP(pendiente)}</span>
                    {p.fecha_esperada && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>vence {formatFecha(p.fecha_esperada)}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Estado por categoría */}
      {bolsillos.length > 0 && (
        <div className="recent-transactions" style={{ marginBottom: 24 }}>
          <h3>Estado por categoría</h3>
          <ul>
            {bolsillos.filter(b => !b.aparte).map(b => {
              const { saldo, comprometido, enMetas, disponible } = fin.porBolsillo(b.id);
              return (
                <li key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>{b.nombre}</span>
                  <div style={{ display: 'flex', gap: 16, fontSize: '0.88rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Saldo: <strong>{formatCLP(saldo)}</strong>
                    </span>
                    {comprometido > 0 && (
                      <span className="text-warning" style={{ fontSize: '0.82rem' }}>
                        Comprometido: <strong>{formatCLP(comprometido)}</strong>
                      </span>
                    )}
                    {enMetas > 0 && (
                      <span style={{ fontSize: '0.82rem', color: 'var(--primary)' }}>
                        En metas: <strong>{formatCLP(enMetas)}</strong>
                      </span>
                    )}
                    <span className={disponible >= 0 ? 'text-success' : 'text-danger'} style={{ fontWeight: 600 }}>
                      Disponible: <strong>{formatCLP(disponible)}</strong>
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Movimientos agrupados por mes */}
      {movimientos.length > 0 ? (
        <div className="recent-transactions">
          <h3>Movimientos por mes</h3>
          <MovimientosPorMes movimientos={movimientos} bolsillos={bolsillos} />
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-muted)', background: 'white', borderRadius: 12 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>📊</div>
          <p>Aún no hay movimientos. Ve a "Mis Categorías" para registrar el primero.</p>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
